// Email Attachment API - API integration for email attachments
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';

// Request/response types
export interface EmailAttachmentCreateData {
  file: File;
  email_id?: string; // Optional, can be set later
  content_id?: string; // For inline attachments
  is_inline?: boolean;
}

/**
 * Factory function to create email attachment API service with ApiClient dependency injection
 * @param apiClient - ApiClient instance from context
 * @returns Service object with all email attachment API methods
 */
export const createEmailAttachmentApi = (apiClient: ApiClient) => ({
  /**
   * Get email attachment details
   * GET /communications/email_attachments/:id
   */
  async getEmailAttachment(attachmentId: string) {
    const response = await apiClient.get<{ data: any }>(
      `/communications/email_attachments/${attachmentId}`
    );
    return response.data;
  },

  /**
   * Upload a new email attachment
   * POST /communications/email_attachments
   * Uses multipart/form-data
   */
  async uploadEmailAttachment(data: EmailAttachmentCreateData) {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.email_id) formData.append('email_id', data.email_id);
    if (data.content_id) formData.append('content_id', data.content_id);
    if (data.is_inline !== undefined) formData.append('is_inline', data.is_inline.toString());

    const response = await apiClient.post<{ data: any }>(
      '/communications/email_attachments',
      formData
    );
    return response.data;
  },

  /**
   * Delete an email attachment (soft delete)
   * DELETE /communications/email_attachments/:id
   */
  async deleteEmailAttachment(attachmentId: string): Promise<void> {
    await apiClient.delete<void>(`/communications/email_attachments/${attachmentId}`);
  },
});

// Type for the service instance
export type EmailAttachmentApi = ReturnType<typeof createEmailAttachmentApi>;
